import CancellationError from '@/common/utils/errors/CancellationError';
import { get, isObject, omit, set } from 'lodash-es';
import uniqid from 'uniqid';

type Action = (...args) => Promise<any>;
type ActionTarget = {
  action: Action,
  actionName: string | Action
} | Action;

interface ActiveEffect {
  id: string
  hash: string
  timeout: ReturnType<typeof setTimeout> | null
  request: Promise<any>
  reject: (nextAction?: Promise<any>) => void
}

interface ActiveEffects {
  [key: string]: ActiveEffect
}

interface ControllableEffectPayload {
  view?: string
  options?: Record<string, any>
}

type SuccessFunctionCallback = (
  resolve: (value: PromiseLike<any>) => void,
  activeEffect: ActiveEffect,
  abortController: AbortController
) => void;

export const activeEffects: ActiveEffects = {};

const hashStore = {};

export const useActionTarget = (actionTarget: ActionTarget) => {
  if (typeof actionTarget === 'function') {
    return { action: actionTarget, actionName: actionTarget };
  }
  return {
    action: actionTarget.action,
    actionName: actionTarget.actionName
  };
};

export const getHash = (actionTarget: ActionTarget, payload: ControllableEffectPayload, withPayload: boolean = true): string => {
  const { action, actionName } = useActionTarget(actionTarget);
  const hashPayload = JSON.stringify(withPayload ? omit(payload, 'options') : {});

  const hashPath = [action, actionName, hashPayload];
  let hashId: string = get(hashStore, hashPath as string[]);

  if (!hashId) {
    hashId = uniqid();
    set(hashStore, hashPath as string[], hashId);
  }
  return hashId;
};

export function clearAction(action: ActiveEffect): void {
  if (!action) {
    return;
  }
  if (action.timeout) {
    clearTimeout(action.timeout);
  }
  if (activeEffects[action.hash] && action.id === activeEffects[action.hash].id) {
    delete activeEffects[action.hash];
  }
}

export function wrapActionWithAbort(requestHash: string, callback: SuccessFunctionCallback): ActiveEffect {
  const activeEffect: ActiveEffect = {
    id: uniqid(),
    hash: requestHash,
    timeout: null,
    request: Promise.resolve(),
    reject: () => { }
  };

  activeEffect.request = new Promise((resolve, reject) => {
    const abortController = new AbortController();

    activeEffect.reject = (nextAction) => {
      if (!activeEffect.timeout) {
        // NOTE (mpetrov): some actions may still not started.
        // We reject promises if they already fired by timeout delay
        abortController.abort();
        reject(new CancellationError());
      } else {
        resolve(nextAction);
      }
      clearAction(activeEffect);
    };

    callback(resolve, activeEffect, abortController);
  });

  return activeEffect;
}

const executeActionTarget = (actionTarget, payload: Record<string, any> = {}, options) => {
  const { action, actionName } = useActionTarget(actionTarget);
  const {
    includeId,
    view,
    marker,
    limit,
    ...params
  } = payload;

  return typeof actionTarget === 'function'
    ? actionTarget(
      !isObject(payload) || Array.isArray(payload) ? payload : params,
      {
        includeId,
        view,
        marker,
        limit,
        ...options
      })
    : action(actionName, { ...payload, options });
};

/**
 * Wraps and dispatches action with debounce and takes the first during it is executing
 * @param  {String} action name to dispatch
 * @param  {Object} request query parameters
 * @param  {Integer} interval to the next request after first response is received (default = 600)
 * @param {Boolean} use payload to match requests (default = true)
 */
export const takeFirst = function (
  actionTarget: ActionTarget,
  payload: ControllableEffectPayload = {},
  delay = 1000,
  withPayload = true
) {
  const requestHash = getHash(actionTarget, payload, withPayload);

  if (activeEffects[requestHash]) {
    return Promise.resolve(activeEffects[requestHash].request);
  }

  const newCancellableAction = wrapActionWithAbort(
    requestHash,
    (resolve, activeEffect, abortController) => resolve(
      executeActionTarget(actionTarget, payload, { abortController, ...payload.options })
        .finally(() => {
          activeEffect.timeout = setTimeout(() => clearAction(newCancellableAction), delay);
        }))
  );

  activeEffects[requestHash] = newCancellableAction;
  return activeEffects[requestHash].request;
};

/**
 * Wraps and dispatches actions with throttle. Takes the leading and last request.
 * @param  {String} actionName - name to dispatch
 * @param  {Object} payload - query parameters
 * @param  {Integer} delay to dispatch the action (default = 300)
 * @param {Boolean} withPayload - whether to use payload for matching requests (default = true)
 */
export const takeLast = function (
  actionTarget: ActionTarget,
  payload: ControllableEffectPayload = {},
  delay = 300,
  withPayload = true
) {
  const requestHash = getHash(actionTarget, payload, withPayload);
  const prevActiveEffect = activeEffects[requestHash];

  const newCancellableAction = wrapActionWithAbort(
    requestHash,
    (resolve, activeEffect, abortController) => {
      const delayedRequest = () => resolve(
        executeActionTarget(actionTarget, payload, { abortController, ...payload.options })
          .catch((err) => {
            if (
              err instanceof CancellationError
              && activeEffects[requestHash]
              && !payload.options?.abortController
            ) {
              // Aborted request should finish together with the last request
              // Here is current request "newCancellableAction" is already cancelled
              // and activeEffects[requestHash] is the last request currently running
              return activeEffects[requestHash].request;
            }

            throw err;
          })
          .finally(() => clearAction(activeEffect))
      );

      if (!activeEffects[requestHash]) {
        delayedRequest();
        return;
      }

      activeEffect.timeout = setTimeout(() => {
        activeEffect.timeout = null;
        delayedRequest();
      }, delay);
    }
  );

  if (prevActiveEffect) {
    prevActiveEffect.reject(newCancellableAction.request);
  }
  activeEffects[requestHash] = newCancellableAction;
  return activeEffects[requestHash].request;
};

export const abortEffect = function (actionTarget: ActionTarget, payload = {}, withPayload = true) {
  const requestHash = getHash(actionTarget, payload, withPayload);
  const activeEffect = activeEffects[requestHash];

  if (activeEffect && typeof activeEffect.reject === 'function') {
    activeEffects[requestHash].reject();
  }
  return Promise.resolve();
};
